import { useState, type FormEvent } from "react";
import ConfirmCodeStep from "../components/ConfirmCodeStep";
import { classifySignupResult, RESEND_COOLDOWN_SECONDS } from "../lib/otp";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase, isSupabaseEnabled } from "../lib/supabaseClient";
import { useParish } from "../lib/parishContext";
import PoweredByFooter from "../components/PoweredByFooter";
import VolunteerBrandMark from "../components/VolunteerBrandMark";
import AuthScreenHeader from "../components/AuthScreenHeader";

const volunteerBrand = <VolunteerBrandMark height={72} />;

const MIN_PASSWORD_LENGTH = 8;

// Maps Supabase's signup-specific errors to plain language. Unlike
// login (lib/session's genericizeCredentialError, which deliberately
// hides whether an email is registered), telling a volunteer "you
// already have an account" on the SIGNUP form is the helpful thing to
// do, not an enumeration risk — it points them at the right screen
// instead of a confusing generic failure.
function describeSignupError(message: string | undefined): string {
  if (!message) return "Something went wrong creating your account. Please try again.";
  const m = message.toLowerCase();
  if (m.includes("already registered") || m.includes("already exists")) {
    return "You already have an account with this email. Try signing in instead.";
  }
  if (m.includes("password")) return message;
  return message;
}

export default function VolunteerSignup() {
  const { parish: slug } = useParams();
  const { parish, loading: parishLoading } = useParish();

  if (!isSupabaseEnabled) {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            brand={volunteerBrand}
            title="Create your account"
            subtitle="Volunteer signup isn't available yet. Please check back soon."
          />
        </div>
      </main>
    );
  }

  return <VolunteerSignupForm slug={slug ?? ""} parishName={parish.display_name} parishId={parish.id} parishLoading={parishLoading} />;
}

type Status =
  | "idle"
  | "submitting"
  | "error"
  // Enter the emailed 6-digit code (confirmation hotfix — codes
  // replace scanner-vulnerable links).
  | "confirm-code"
  // signUp said this email already has a FINISHED account.
  | "already-confirmed"
  | "signed-in";

function VolunteerSignupForm({
  slug,
  parishName,
  parishId,
  parishLoading,
}: {
  slug: string;
  parishName: string;
  parishId: string | undefined;
  parishLoading: boolean;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;

    if (!parishId) {
      setStatus("error");
      setError(
        "This signup link isn't working right now. Please contact your parish coordinator for a fresh link.",
      );
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setStatus("error");
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setStatus("submitting");
    setError(null);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Read by the handle_new_volunteer() DB trigger (migration
        // 0006) to create the matching volunteer_accounts row in the
        // same transaction as the auth signup. parish_name feeds the
        // Supabase auth-email templates ({{ .Data.parish_name }});
        // they fall back to neutral wording when it's absent.
        data: {
          role: "volunteer",
          parish_id: parishId,
          full_name: name.trim(),
          parish_name: parishName,
        },
        emailRedirectTo: `${window.location.origin}/${slug}/login`,
      },
    });

    // Live-probed Supabase semantics (lib/otp.ts): re-signup on an
    // UNCONFIRMED email succeeds and auto-resends the code — exactly
    // what a volunteer holding a dead invite link needs. Inside the
    // 60s send window it's a rate-limit error instead; a FINISHED
    // account comes back obfuscated (identities: []).
    const outcome = classifySignupResult({
      errorMessage: signUpError?.message ?? null,
      identitiesLength: data?.user ? (data.user.identities ?? []).length : null,
      hasSession: Boolean(data?.session),
    });
    switch (outcome.kind) {
      case "signed-in":
        setStatus("signed-in");
        return;
      case "code-sent":
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setStatus("confirm-code");
        return;
      case "code-recently-sent":
        // Their earlier attempt (or the invite retry) already sent a
        // code moments ago — go straight to code entry, resend locked
        // for the server's own retry window.
        setResendCooldown(outcome.retryAfter);
        setStatus("confirm-code");
        return;
      case "already-confirmed":
        setStatus("already-confirmed");
        return;
      case "error":
        setStatus("error");
        setError(describeSignupError(outcome.message));
        return;
    }
  }

  if (status === "confirm-code") {
    return (
      <ConfirmCodeStep
        email={email}
        brand={volunteerBrand}
        initialCooldown={resendCooldown}
        onVerified={() => setStatus("signed-in")}
        onBack={() => setStatus("idle")}
        footer={<PoweredByFooter />}
      />
    );
  }

  if (status === "already-confirmed") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader brand={volunteerBrand} title="You're already set up" />
          <div className="sign-in__confirmation">
            <p>
              <strong>{email}</strong> already has a finished account. Sign in with your
              email and password. Forgot the password? The sign-in page can send you a
              reset link.
            </p>
          </div>
          <button type="button" className="btn" onClick={() => navigate(`/${slug}/login`)}>
            Go to sign in
          </button>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  if (status === "signed-in") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader brand={volunteerBrand} title="You're all set" />
          <div className="sign-in__confirmation">
            <p>
              Your account is set up. Your Mass assignments are managed by your parish
              coordinator and will appear on your dashboard once scheduled.
            </p>
          </div>
          <button type="button" className="btn" onClick={() => navigate(`/${slug}/dashboard`)}>
            Go to your dashboard
          </button>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          brand={volunteerBrand}
          title="Create your account"
          subtitle={`Create your volunteer account${parishName ? ` for ${parishName}` : ""} to see your Mass schedule online.`}
        />

        <form className="sign-in__form" onSubmit={submit}>
          <label className="sign-in__field">
            <span className="sign-in__label">Name</span>
            <input
              type="text"
              className="sign-in__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
              autoFocus
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Email</span>
            <input
              type="email"
              className="sign-in__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
            {/* The portal matches this address against the volunteer's
                email in MSP to find their assignments — a different
                address means an empty "pending" dashboard. */}
            <span className="sign-in__hint">
              Use the same email your parish scheduler has on file for you. It's how we
              find your Mass schedule.
            </span>
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Password</span>
            <input
              type="password"
              className="sign-in__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
          </label>
          <button
            type="submit"
            className="btn"
            disabled={status === "submitting" || parishLoading}
          >
            {status === "submitting" ? "Creating account…" : "Create account"}
          </button>
          {error ? <p className="sign-in__error">{error}</p> : null}
        </form>

        <div className="sign-in__alt">
          <span className="sign-in__lede">Already have an account? </span>
          <Link className="sign-in__link" to={`/${slug}/login`}>
            Sign in
          </Link>
        </div>
      </div>
      <PoweredByFooter />
    </main>
  );
}
